import React, { useState } from 'react';
import * as Sentry from "@sentry/react";

const SentryTest = () => {
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 5000); // Clear after 5 seconds
  };

  const throwRuntimeError = async () => {
    try {
      // This will throw a runtime error
      const nonExistentObject: any = undefined;
      nonExistentObject.someProperty.anotherProperty;
    } catch (error) {
      Sentry.captureException(error);
      showFeedback('success', 'Runtime error captured by Sentry!');
    }
  };

  const throwAsyncError = async () => {
    try {
      // This will throw an async error
      await Promise.reject(new Error('This is an async error!'));
    } catch (error) {
      Sentry.captureException(error);
      showFeedback('success', 'Async error captured by Sentry!');
    }
  };

  const throwCustomError = () => {
    try {
      // This will throw a custom error
      throw new Error('This is a custom error with additional context!');
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          customContext: 'This is additional context for the error',
          timestamp: new Date().toISOString(),
        },
      });
      showFeedback('success', 'Custom error captured by Sentry!');
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Sentry Test Page</h1>
      <p className="mb-4">Click the buttons below to test different types of Sentry error reporting:</p>
      
      {feedback && (
        <div className={`mb-4 p-4 rounded ${
          feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {feedback.message}
        </div>
      )}

      <div className="space-y-4">
        <button 
          onClick={throwRuntimeError}
          className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded mr-4"
        >
          Test Runtime Error
        </button>

        <button 
          onClick={throwAsyncError}
          className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded mr-4"
        >
          Test Async Error
        </button>

        <button 
          onClick={throwCustomError}
          className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
        >
          Test Custom Error
        </button>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-2">What each button does:</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Runtime Error:</strong> Attempts to access a property of an undefined object</li>
          <li><strong>Async Error:</strong> Throws an error in an async context</li>
          <li><strong>Custom Error:</strong> Throws a custom error with additional context</li>
        </ul>
      </div>
    </div>
  );
};

export default SentryTest; 